import { useShallow } from 'zustand/react/shallow';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDisclosure } from '@mantine/hooks';
import { Modal } from '@mantine/core';
import { css, useTheme } from '@emotion/react';
import { Icon } from '../../Icon/Icon';
import {
  Button,
  globals,
  handleDLMiniCartClick,
  Link,
  NEW_CART_PAGE,
  OLD_CART_PAGE,
  TrendingProductItems,
  useCheckoutRedirect,
} from 'common-ui';
import { useCartStore } from 'common-state';
import { selectCartData } from 'common-state/src/stores/cartStore';
import type { MiniCartModalProps } from 'tsconfig/types';
import MiniCartLayout from '../MiniCartLayout';
import ViewCart from '../components/ViewCart';
import TaxMessage from './components/TaxMessage';
import { useMiniCartLogic } from './hooks/useMiniCartLogic';
import { handleRedirectionClick } from '../../../utils/cart/miniCartUtils';

const Title = () => {
  let titleText = 'In your cart';

  if (globals.country === 'uk') {
    titleText = 'Added to basket';
  } else if (globals.brandId === 'newstf') {
    titleText = 'Added to cart';
  }

  return (
    <div className="mini-cart-header">
      <Icon kind="tick-circle" size="l" />
      <h3>{titleText}</h3>
    </div>
  );
};

const MiniCartModal: React.FC<MiniCartModalProps> = ({
  show,
  size = 'xl',
  onClose,
  selectedItemCode,
  qty,
}: MiniCartModalProps) => {
  const theme = useTheme();
  const [opened, { close }] = useDisclosure(false);
  const { cartItems } = useCartStore(useShallow(selectCartData));
  const [animationClass, setAnimationClass] = useState('slide-in');
  const [currentMiniCartItemCode, setCurrentMiniCartItemCode] = useState<string>('');
  const [modalContentNode, setModalContentNode] = useState<HTMLElement | null>(null);
  const [modalTopActionsNode, setModalTopActionsNode] = useState<HTMLElement | null>(null);
  const redirectCheckoutRoute = useCheckoutRedirect();
  const isUK = globals.country === 'uk';
  const redirectLink = globals?.useNewCartAPI ? NEW_CART_PAGE : OLD_CART_PAGE;

  const { selectedItem, showCheckoutButton, isFreeGiftLineItem, isGreatPick } = useMiniCartLogic(
    cartItems,
    selectedItemCode,
    currentMiniCartItemCode,
    setCurrentMiniCartItemCode,
  );

  const MiniCartWrapper = css`
    ${theme.widgets.Cart.MiniCart || ''}
    .mantine-Modal-content {
      animation: ${animationClass === 'slide-in' ? 'slideInFromRight' : 'slideOutToRight'} 0.2s ease-in-out !important;
    }
  `;

  const handleClose = () => {
    setAnimationClass('slide-out');
    const timer = setTimeout(() => {
      close();
      onClose();
    }, 200);
    return () => clearTimeout(timer);
  };

  const handleMiniCartClickEvents = (eventType: string) => {
    eventType && handleDLMiniCartClick('miniCartClick', eventType);
  };

  useEffect(() => {
    if (!show || !isUK) {
      setModalContentNode(null);
      setModalTopActionsNode(null);
      return;
    }

    let rafId = 0;
    let attempts = 0;
    let topActionsNode: HTMLElement | null = null;
    let createdTopActionsNode = false;

    const findModalContentNode = () => {
      const node = document.querySelector(
        '.mantine-Modal-content-minicart .mantine-Modal-content, .mantine-Modal-content-minicart.mantine-Modal-content',
      );

      if (node instanceof HTMLElement) {
        setModalContentNode(node);

        const existingTopActionsNode = node.querySelector('.minicart-top-actions-slot');
        if (existingTopActionsNode instanceof HTMLElement) {
          topActionsNode = existingTopActionsNode;
        } else {
          topActionsNode = document.createElement('div');
          topActionsNode.className = 'minicart-top-actions-slot';
          topActionsNode.dataset.owner = 'MiniCartModal';
          createdTopActionsNode = true;
          node.insertBefore(topActionsNode, node.firstChild);
        }

        setModalTopActionsNode(topActionsNode);
        return;
      }

      attempts += 1;
      if (attempts < 10) {
        rafId = requestAnimationFrame(findModalContentNode);
      }
    };

    findModalContentNode();

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      if (createdTopActionsNode && topActionsNode) {
        topActionsNode.remove();
      }
    };
  }, [show, isUK]);

  if (!show) {
    return null;
  }

  return selectedItem?.length ? (
    <Modal
      css={MiniCartWrapper}
      opened={opened || !!show}
      className={`mantine-Modal-content-minicart vine-react-app ${animationClass}`}
      onClose={() => {
        handleClose();
        handleMiniCartClickEvents('closeMiniCartClick');
      }}
      title={<Title />}
      size={size}
    >
      <div style={{ position: 'relative' }}>
        <MiniCartLayout
          lineItems={selectedItem}
          selectedItemCode={selectedItemCode}
          totalLineItem={cartItems?.totalItemQuantity ?? 0}
          onClose={handleClose}
          isFreeGiftLineItem={isFreeGiftLineItem}
          isGreatPick={isGreatPick}
          qty={qty}
          showCheckoutButton={showCheckoutButton}
          showActionButtons={!isUK}
          showContinueShopping={!isUK}
        />
        {globals.country !== 'us' && globals.brandId !== 'amx' && (
          <TrendingProductItems showImageZoom={false} isMiniCart={true} widgetTitle="Top Trending Wines" />
        )}
      </div>
      {!isUK && (
        <button
          className="continue-shopping desktop button-link"
          data-testid="continue-shopping"
          onClick={() => {
            handleClose();
            handleMiniCartClickEvents('continueShoppingtMiniCartClick');
          }}
        >
          <span className="continue-shopping-text">continue shopping</span>
        </button>
      )}
      <TaxMessage selectedItem={selectedItem} />
      {isUK &&
        modalTopActionsNode &&
        createPortal(
          <div className="minicart-top-actions">
            <button
              className="continue-shopping top button-link"
              data-testid="continue-shopping"
              onClick={() => {
                handleClose();
                handleMiniCartClickEvents('continueShoppingtMiniCartClick');
              }}
            >
              <span className="cs-arrow">‹</span>
              <span className="continue-shopping-text">continue shopping</span>
            </button>
          </div>,
          modalTopActionsNode,
        )}
      {isUK &&
        modalContentNode &&
        createPortal(
          <div className="button-container minicart-sticky-cta">
            <Link href={redirectLink} prefetch={false}>
              <ViewCart
                totalLineItem={cartItems?.totalItemQuantity ?? 0}
                redirectLink={redirectLink}
                onClose={handleClose}
                isUK={isUK}
              />
            </Link>
            {showCheckoutButton && (
              <a href={redirectCheckoutRoute}>
                <Button
                  kind="primary"
                  data-testid="checkout-button"
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    handleRedirectionClick(e, redirectCheckoutRoute, handleClose);
                    handleMiniCartClickEvents('checkoutMiniCartClick');
                  }}
                >
                  CHECKOUT
                </Button>
              </a>
            )}
          </div>,
          modalContentNode,
        )}
    </Modal>
  ) : null;
};

export default MiniCartModal;
